/**
 * Module dependencies.
 */

import { Button, Modal } from 'antd';
import { Form } from 'app/components/organisms/crud/crud-form';
import { FormInputField } from 'app/components/organisms/crud/form-components/form-input-field';
import { requiredRule } from 'app/core/utils/field-rules';
import { usePostConsentPhoneNumber } from 'app/hooks/app-requests/consent-wizard/use-post-phone-number';
import { usePostConsentPhoneNumberConfirm } from 'app/hooks/app-requests/consent-wizard/use-post-phone-number-confirm';
import { useGetPharmacyIntegrations } from 'app/hooks/app-requests/pharmacies/use-get-pharmacies-integrations';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

/**
 * `Props` type.
 */

type Props = {
  refresh: () => Promise<any>;
};

/**
 * Export `AddConsentWizardModal` component.
 */

export function AddConsentWizardModal({ refresh }: Props): JSX.Element | null {
  const [isVisible, setVisible] = useState<boolean>(false);
  const [step2, setStep2] = useState<boolean>(false);
  const params = useParams();
  const [translate] = useTranslation();
  const form1 = useForm<any>({
    values: {},
    mode: 'all'
  });

  const form2 = useForm<any>({
    values: {
      phoneNumber: null,
      externalUserId: null
    },
    mode: 'all'
  });

  const { data: integrations } = useGetPharmacyIntegrations(params.pharmacyId);
  const { mutateAsync: postPhoneNumber } = usePostConsentPhoneNumber();
  const { mutateAsync: postPhoneNumberConfirm } = usePostConsentPhoneNumberConfirm();

  const handleSubmit = useCallback(
    async (data: any) => {
      if (integrations?.[0]?.apiKey) {
        try {
          await postPhoneNumber({
            apiKey: integrations?.[0]?.apiKey,
            phoneNumber: data.phoneNumber,
            externalUserId: data.externalUserId
          });

          setStep2(true);

          form2.setValue('phoneNumber', data.phoneNumber);
          form2.setValue('externalUserId', data.externalUserId);
        } catch (error) {
          console.error('Error posting phone number:', error);
        }
      }
    },
    [form2, integrations, postPhoneNumber]
  );

  const handleSubmitCode = useCallback(
    async (data: any) => {
      try {
        if (integrations?.[0]?.apiKey) {
          await postPhoneNumberConfirm({
            apiKey: integrations?.[0]?.apiKey,
            phoneNumber: data.phoneNumber,
            pin: data.code
          });

          await refresh();

          setVisible(false);
        }
      } catch (errr) {
        console.error('Error confirming phone number:', errr);
      }
    },
    [integrations, postPhoneNumberConfirm, refresh]
  );

  useEffect(() => {
    if (!isVisible) {
      form1.setValue('phoneNumber', '');
      form1.setValue('externalUserId', '');
      form2.setValue('code', '');
      setStep2(false);
    }
  }, [form1, form2, isVisible]);

  if (!integrations || integrations.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        onClick={() => {
          setVisible(true);
        }}
        size={'large'}
      >
        {translate('addConsentWizard.openAction')}
      </Button>

      <Modal
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={() => {
          setVisible(false);
        }}
        onOk={() => {
          setVisible(false);
        }}
        open={isVisible}
        title={translate('addConsentWizard.title')}
        width={'40%'}
      >
        {!step2 && (
          <Form form={form1} onSubmit={handleSubmit} submitLabel={'NEXT'}>
            <>
              <FormInputField
                form={form1}
                item={{
                  label: translate('addConsentWizard.labels.phoneNumber'),
                  rules: requiredRule(translate),
                  name: 'phoneNumber',
                  type: 'inputField'
                }}
              />

              <FormInputField
                form={form1}
                item={{
                  label: translate('addConsentWizard.labels.externalUserId'),
                  rules: requiredRule(translate),
                  name: 'externalUserId',
                  type: 'inputField'
                }}
              />
            </>
          </Form>
        )}

        {!!step2 && (
          <Form form={form2 as any} onSubmit={handleSubmitCode} submitLabel={'REGISTER'}>
            <>
              <FormInputField
                form={form1}
                item={{
                  disabled: true,
                  label: translate('addConsentWizard.labels.phoneNumber'),
                  rules: requiredRule(translate),
                  name: 'phoneNumber',
                  type: 'inputField'
                }}
              />

              <FormInputField
                form={form1}
                item={{
                  disabled: true,
                  label: translate('addConsentWizard.labels.externalUserId'),
                  rules: requiredRule(translate),
                  name: 'externalUserId',
                  type: 'inputField'
                }}
              />

              <FormInputField
                form={form2 as any}
                item={{
                  label: translate('addConsentWizard.labels.code'),
                  rules: undefined,
                  name: 'code',
                  type: 'inputField'
                }}
              />
            </>
          </Form>
        )}
      </Modal>
    </>
  );
}
